(() => {
  const ROOT_SELECTOR = '#header-group';
  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_TIME = 220;

  const rootElement = document.querySelector(ROOT_SELECTOR);

  if (!rootElement) {
    console.warn('[DobroWita search] Nie znaleziono #header-group.');
    return;
  }

  const inputSelectors = [
    'input[type="search"]',
    'input[name="q"]',
    'input[placeholder*="Szukaj"]',
    'input[placeholder*="szukaj"]'
  ];

  const searchInputs = Array.from(
    new Set(
      inputSelectors.flatMap((selector) =>
        Array.from(rootElement.querySelectorAll(selector))
      )
    )
  );

  if (!searchInputs.length) {
    console.warn('[DobroWita search] Nie znaleziono pola wyszukiwania.');
    return;
  }

  const shopRoot = window.Shopify?.routes?.root || '/';
  const localizedRoot = shopRoot.endsWith('/')
    ? shopRoot
    : `${shopRoot}/`;

  const searchUrl = `${localizedRoot}search`;
  const predictiveSearchUrl =
    `${localizedRoot}search/suggest.json`;

  const currency =
    window.Shopify?.currency?.active ||
    window.Shopify?.currency ||
    'PLN';

  let activeInput = null;
  let activeForm = null;
  let activeAnchor = null;
  let activeIndex = -1;
  let activeRequest = null;
  let debounceTimer = null;
  let lastQuery = '';

  const panel = document.createElement('div');

  panel.className = 'dw-search-panel';
  panel.id = 'DWDynamicSearchPanel';
  panel.setAttribute('aria-hidden', 'true');
  panel.setAttribute('role', 'listbox');
  panel.setAttribute(
    'aria-label',
    'Podpowiedzi wyszukiwania'
  );

  document.body.appendChild(panel);

  const escapeHtml = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const escapeAttribute = escapeHtml;

  const formatMoney = (value) => {
    const amount = Number(value);

    if (!Number.isFinite(amount)) {
      return '';
    }

    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency:
        typeof currency === 'string'
          ? currency
          : 'PLN'
    }).format(amount);
  };

  const getAnchor = (input) => {
    const form = input.closest('form');

    if (form) {
      return form;
    }

    return (
      input.closest(
        '.dw-header__search, .header__search, [class*="search"]'
      ) ||
      input.parentElement ||
      input
    );
  };

  const getFocusableResults = () =>
    Array.from(
      panel.querySelectorAll(
        'a[href][role="option"], a[href].dw-predictive-search__all'
      )
    );

  const positionPanel = () => {
    if (
      !activeInput ||
      !activeAnchor ||
      !panel.classList.contains('is-open')
    ) {
      return;
    }

    const inputRect =
      activeInput.getBoundingClientRect();

    const anchorRect =
      activeAnchor.getBoundingClientRect();

    const isMobile = window.matchMedia(
      '(max-width: 749px)'
    ).matches;

    panel.style.position = 'fixed';

    if (isMobile) {
      panel.style.left = '10px';
      panel.style.right = '10px';

      panel.style.top = `${Math.min(
        window.innerHeight - 90,
        inputRect.bottom + 8
      )}px`;

      panel.style.width = 'auto';

      panel.style.maxHeight = `${Math.max(
        220,
        window.innerHeight -
          inputRect.bottom -
          24
      )}px`;

      return;
    }

    const desiredWidth = Math.max(
      480,
      Math.min(720, anchorRect.width)
    );

    const maximumLeft = Math.max(
      10,
      window.innerWidth -
        desiredWidth -
        10
    );

    const left = Math.min(
      Math.max(10, anchorRect.left),
      maximumLeft
    );

    panel.style.left = `${left}px`;
    panel.style.right = 'auto';
    panel.style.top =
      `${anchorRect.bottom + 1}px`;

    panel.style.width = `${desiredWidth}px`;

    panel.style.maxHeight = `${Math.max(
      260,
      window.innerHeight -
        anchorRect.bottom -
        20
    )}px`;
  };

  const openPanel = () => {
    panel.classList.add('is-open');
    panel.setAttribute(
      'aria-hidden',
      'false'
    );

    if (activeInput) {
      activeInput.setAttribute(
        'aria-expanded',
        'true'
      );
    }

    positionPanel();
  };

  const closePanel = () => {
    panel.classList.remove(
      'is-open',
      'is-loading'
    );

    panel.setAttribute(
      'aria-hidden',
      'true'
    );

    if (activeInput) {
      activeInput.setAttribute(
        'aria-expanded',
        'false'
      );
    }

    activeIndex = -1;
    activeRequest?.abort();
  };

  const setFocusedResult = (nextIndex) => {
    const results = getFocusableResults();

    if (!results.length) {
      activeIndex = -1;
      return;
    }

    results.forEach((result) => {
      result.classList.remove('is-focused');
      result.setAttribute(
        'aria-selected',
        'false'
      );
    });

    activeIndex =
      (nextIndex + results.length) %
      results.length;

    const result = results[activeIndex];

    result.classList.add('is-focused');

    result.setAttribute(
      'aria-selected',
      'true'
    );

    result.scrollIntoView({
      block: 'nearest'
    });
  };

  const productImageMarkup = (product) => {
    const imageUrl =
      product.featured_image?.url ||
      product.image ||
      '';

    if (!imageUrl) {
      return `
        <span
          class="dw-predictive-search__placeholder"
          aria-hidden="true"
        >
          □
        </span>
      `;
    }

    return `
      <img
        class="dw-predictive-search__image"
        src="${escapeAttribute(imageUrl)}"
        alt="${escapeAttribute(
          product.featured_image?.alt ||
          product.title ||
          ''
        )}"
        loading="lazy"
      >
    `;
  };

  const productMarkup = (product) => {
    const comparePrice = Number(
      product.compare_at_price_min || 0
    );

    const price = Number(
      product.price ||
      product.price_min ||
      0
    );

    const compareMarkup =
      comparePrice > price
        ? `
          <del>
            ${escapeHtml(
              formatMoney(comparePrice)
            )}
          </del>
        `
        : '';

    const vendorMarkup = product.vendor
      ? `
        <span
          class="dw-predictive-search__vendor"
        >
          ${escapeHtml(product.vendor)}
        </span>
      `
      : '';

    return `
      <a
        class="dw-predictive-search__product"
        href="${escapeAttribute(product.url)}"
        role="option"
        aria-selected="false"
      >
        <span
          class="dw-predictive-search__image-wrap"
        >
          ${productImageMarkup(product)}
        </span>

        <span
          class="dw-predictive-search__product-copy"
        >
          ${vendorMarkup}

          <strong>
            ${escapeHtml(product.title)}
          </strong>

          <span
            class="dw-predictive-search__price"
          >
            ${escapeHtml(formatMoney(price))}
            ${compareMarkup}
          </span>
        </span>
      </a>
    `;
  };

  const simpleLinkMarkup = (resource) => `
    <a
      href="${escapeAttribute(resource.url)}"
      role="option"
      aria-selected="false"
    >
      <span aria-hidden="true">→</span>

      <span>
        ${escapeHtml(
          resource.title ||
          resource.text ||
          ''
        )}
      </span>
    </a>
  `;

  const renderResults = (
    query,
    results
  ) => {
    const products = (
      results.products || []
    ).slice(0, 6);

    const collections = (
      results.collections || []
    ).slice(0, 4);

    const pages = (
      results.pages || []
    ).slice(0, 3);

    const articles = (
      results.articles || []
    ).slice(0, 3);

    const hasResults =
      products.length ||
      collections.length ||
      pages.length ||
      articles.length;

    const productsGroup = products.length
      ? `
        <div
          class="dw-predictive-search__group"
        >
          <div
            class="dw-predictive-search__heading"
          >
            <span>Produkty</span>
            <span>${products.length}</span>
          </div>

          <div
            class="dw-predictive-search__products"
          >
            ${products
              .map(productMarkup)
              .join('')}
          </div>
        </div>
      `
      : '';

    const collectionsGroup =
      collections.length
        ? `
          <div
            class="dw-predictive-search__group dw-predictive-search__group--links"
          >
            <div
              class="dw-predictive-search__heading"
            >
              <span>Kategorie</span>
            </div>

            <div
              class="dw-predictive-search__links"
            >
              ${collections
                .map(simpleLinkMarkup)
                .join('')}
            </div>
          </div>
        `
        : '';

    const informationResources = [
      ...pages,
      ...articles
    ];

    const informationGroup =
      informationResources.length
        ? `
          <div
            class="dw-predictive-search__group dw-predictive-search__group--links"
          >
            <div
              class="dw-predictive-search__heading"
            >
              <span>
                Poradniki i informacje
              </span>
            </div>

            <div
              class="dw-predictive-search__links"
            >
              ${informationResources
                .map(simpleLinkMarkup)
                .join('')}
            </div>
          </div>
        `
        : '';

    const emptyMarkup = !hasResults
      ? `
        <div
          class="dw-predictive-search__empty"
        >
          Brak podpowiedzi dla
          „${escapeHtml(query)}”.
        </div>
      `
      : '';

    const allResultsUrl =
      `${searchUrl}?q=${encodeURIComponent(
        query
      )}` +
      '&options%5Bprefix%5D=last';

    panel.innerHTML = `
      <div class="dw-predictive-search">
        ${productsGroup}
        ${collectionsGroup}
        ${informationGroup}
        ${emptyMarkup}

        <a
          class="dw-predictive-search__all"
          href="${escapeAttribute(
            allResultsUrl
          )}"
          role="option"
          aria-selected="false"
        >
          <span>
            Pokaż wszystkie wyniki dla
            <strong>
              „${escapeHtml(query)}”
            </strong>
          </span>

          <span aria-hidden="true">→</span>
        </a>
      </div>
    `;

    activeIndex = -1;
    openPanel();
  };

  const showError = (query) => {
    const allResultsUrl =
      `${searchUrl}?q=${encodeURIComponent(
        query
      )}` +
      '&options%5Bprefix%5D=last';

    panel.innerHTML = `
      <div class="dw-predictive-search">
        <div
          class="dw-predictive-search__empty"
        >
          Nie udało się pobrać podpowiedzi.
        </div>

        <a
          class="dw-predictive-search__all"
          href="${escapeAttribute(
            allResultsUrl
          )}"
          role="option"
          aria-selected="false"
        >
          <span>
            Pokaż wyniki wyszukiwania
          </span>

          <span aria-hidden="true">→</span>
        </a>
      </div>
    `;

    openPanel();
  };

  const fetchResults = async (query) => {
    activeRequest?.abort();

    activeRequest =
      new AbortController();

    panel.classList.add('is-loading');

    const parameters = new URLSearchParams({
      q: query,
      'resources[type]':
        'product,collection,page,article',
      'resources[limit]': '8',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]':
        'last'
    });

    try {
      const response = await fetch(
        `${predictiveSearchUrl}?${parameters.toString()}`,
        {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          },
          signal: activeRequest.signal
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.description ||
          data.message ||
          `Błąd wyszukiwania: ${response.status}`
        );
      }

      const results =
        data.resources?.results || {};

      lastQuery = query;

      renderResults(
        query,
        results
      );
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(
          '[DobroWita search]',
          error
        );

        showError(query);
      }
    } finally {
      panel.classList.remove(
        'is-loading'
      );
    }
  };

  const scheduleSearch = (input) => {
    activeInput = input;
    activeForm = input.closest('form');
    activeAnchor = getAnchor(input);

    const query = input.value.trim();

    window.clearTimeout(debounceTimer);

    if (
      query.length <
      MIN_QUERY_LENGTH
    ) {
      lastQuery = '';
      closePanel();
      return;
    }

    debounceTimer = window.setTimeout(
      () => {
        fetchResults(query);
      },
      DEBOUNCE_TIME
    );
  };

  const configureForm = (input) => {
    const form = input.closest('form');

    input.type = 'search';
    input.name = 'q';
    input.autocomplete = 'off';

    input.setAttribute(
      'role',
      'combobox'
    );

    input.setAttribute(
      'aria-autocomplete',
      'list'
    );

    input.setAttribute(
      'aria-expanded',
      'false'
    );

    input.setAttribute(
      'aria-controls',
      panel.id
    );

    if (!form) {
      return;
    }

    form.action = searchUrl;
    form.method = 'get';

    form.setAttribute(
      'role',
      'search'
    );

    if (
      !form.querySelector(
        'input[name="options[prefix]"]'
      )
    ) {
      const prefixInput =
        document.createElement('input');

      prefixInput.type = 'hidden';
      prefixInput.name =
        'options[prefix]';

      prefixInput.value = 'last';

      form.appendChild(prefixInput);
    }
  };

  const handleInputKeydown = (event) => {
    if (event.key === 'Escape') {
      closePanel();
      return;
    }

    if (
      !panel.classList.contains(
        'is-open'
      )
    ) {
      if (
        event.key === 'Enter' &&
        !event.currentTarget.closest(
          'form'
        )
      ) {
        const query =
          event.currentTarget.value.trim();

        if (query) {
          event.preventDefault();

          window.location.href =
            `${searchUrl}?q=${encodeURIComponent(
              query
            )}` +
            '&options%5Bprefix%5D=last';
        }
      }

      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      setFocusedResult(
        activeIndex + 1
      );

      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      setFocusedResult(
        activeIndex - 1
      );

      return;
    }

    if (
      event.key === 'Enter' &&
      activeIndex >= 0
    ) {
      const selectedResult =
        getFocusableResults()[
          activeIndex
        ];

      if (selectedResult) {
        event.preventDefault();
        selectedResult.click();
      }
    }
  };

  searchInputs.forEach((input) => {
    configureForm(input);

    input.addEventListener(
      'input',
      () => {
        scheduleSearch(input);
      }
    );

    input.addEventListener(
      'focus',
      () => {
        activeInput = input;
        activeForm =
          input.closest('form');

        activeAnchor =
          getAnchor(input);

        const query =
          input.value.trim();

        if (
          query.length <
          MIN_QUERY_LENGTH
        ) {
          return;
        }

        if (
          query === lastQuery &&
          panel.innerHTML.trim()
        ) {
          openPanel();
        } else {
          scheduleSearch(input);
        }
      }
    );

    input.addEventListener(
      'keydown',
      handleInputKeydown
    );
  });

  document.addEventListener(
    'click',
    (event) => {
      const clickInsidePanel =
        panel.contains(event.target);

      const clickInsideInput =
        searchInputs.some(
          (input) =>
            input === event.target ||
            input.contains?.(
              event.target
            )
        );

      const clickInsideForm =
        searchInputs.some((input) =>
          input
            .closest('form')
            ?.contains(event.target)
        );

      if (
        !clickInsidePanel &&
        !clickInsideInput &&
        !clickInsideForm
      ) {
        closePanel();
      }
    }
  );

  window.addEventListener(
    'resize',
    positionPanel,
    {
      passive: true
    }
  );

  window.addEventListener(
    'scroll',
    positionPanel,
    {
      passive: true
    }
  );
})();